import { useQuery } from "@tanstack/react-query";
import {
  Paper,
  Title,
  Image,
  Grid,
  Text,
  Anchor,
  Loader,
  Alert,
  Container,
  Divider,
  Center
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import React from "react";

const DataPoint: React.FC<{ title: any; value: any }> = ({ title, value }) => (
  <Grid.Col span="auto">
    <Title order={3}>{title}</Title>
    <Text>{value}</Text>
  </Grid.Col>
);

const options = { hour12: true, hour: "numeric", minute: "numeric" } as const;
const formatDate = (date: any) =>
  new Date(date).toLocaleTimeString("en-US", options);

export default function App() {
  const geolocationResult: any = useQuery({
    queryKey: ["geolocation"],
    queryFn: () =>
      new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
          reject("Geolocation is not supported by this browser.");
        }
      })
  });

  const pointsResult = useQuery({
    enabled: !geolocationResult.error && !geolocationResult.isLoading,
    queryKey: ["points"],
    queryFn: () =>
      fetch(
        `https://api.weather.gov/points/${geolocationResult.data.coords.latitude},${geolocationResult.data.coords.longitude}`,
        {
          headers: {
            "User-Agent": "(andrueanderson.com, andrue.anderson.cs@gmail.com)"
          }
        }
      ).then((res) => res.json())
  });

  const forecastResult = useQuery({
    enabled: !pointsResult.error && !pointsResult.isLoading,
    queryKey: ["forecast"],
    queryFn: () =>
      fetch(pointsResult.data.properties.forecast).then((res) => res.json())
  });

  const requests = [geolocationResult, pointsResult, forecastResult];

  if (requests.some((r) => r.error))
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Error Loading Weather Data"
        color="red"
      >
        Uh oh, looks like we aren't able to load your weather data right now.
        Try refreshing the page. Make sure you allow permissions for the
        geolocation API!
      </Alert>
    );

  return (
    <Container size="lg">
      <Paper m="xs" p="xs" withBorder>
        <Title order={1} align="center" mt="xs" mb="xl">
          A Local Weather App
        </Title>
        {requests.some((r) => r.isLoading) ? (
          <Center mb="xl">
            <Loader />
          </Center>
        ) : (
          forecastResult.data.properties.periods.map((period: any) => (
            <Paper withBorder p="md" mb="xs" shadow="md">
              <Grid columns={6}>
                <Grid.Col span="auto">
                  <Image src={period.icon} height={75} width={75} />
                </Grid.Col>
                <DataPoint
                  title="Time"
                  value={
                    <>
                      <Text>{period.name}</Text>
                      <Text>{formatDate(period.startTime)}</Text>
                    </>
                  }
                />
                <DataPoint
                  title="Rain"
                  value={`${period.probabilityOfPrecipitation.value}%`}
                />
                <DataPoint
                  title="Humidity"
                  value={`${period.relativeHumidity.value}%`}
                />
                <DataPoint
                  title="Temperature"
                  value={`${period.temperature}F`}
                />
                <DataPoint
                  title="Wind"
                  value={`${period.windDirection} at ${period.windSpeed}`}
                />
              </Grid>
              <Divider mt="md" />
              <Title mt="md" order={4}>
                Forecast
              </Title>
              <Text>{period.detailedForecast}</Text>
            </Paper>
          ))
        )}
        <Title order={5} align="center" color="dimmed">
          built by{" "}
          <Anchor href="https://andrueanderson.com" target="_blank">
            Andrue Anderson
          </Anchor>
        </Title>
      </Paper>
    </Container>
  );
}
